import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useCogfy, useCollections } from '../../../../../../hooks'
import { UpdateChatMiddlewareCommand } from '@indigohive/cogfy-types/endpoints/updateChatMiddleware'
import { FieldType, UUID } from '@indigohive/cogfy-types'
import { GetChatMiddlewaresResultData } from '@indigohive/cogfy-types/endpoints/getChatMiddlewares'
import { ChatMiddlewareDataForm } from '../../../../../../components/ChatMiddlewareDataForm'
import { GetCollectionsResultData } from '@indigohive/cogfy-types/endpoints/getCollections'
import { ChatMiddlewareConditionForm } from '../../../../../../components/ChatMiddlewareConditionForm'
import { AutogeneratedForm } from '../../../../../../components/AutogeneratedForm'

export type EditChatMiddlewareFormProps = {
  collectionId: UUID
  fieldId: UUID
  middleware: GetChatMiddlewaresResultData
  fields?: {
    id: UUID
    name: string
    type: FieldType
  }[]
  collections?: GetCollectionsResultData[]
}

export function EditChatMiddlewareForm (props: EditChatMiddlewareFormProps) {
  const { fieldId, middleware, collectionId } = props
  const [data, setData] = useState(middleware.data)
  const [condition, setCondition] = useState(middleware.condition)

  const client = useCogfy()
  const queryClient = useQueryClient()
  const updateMiddleware = useMutation({
    mutationFn: (data: UpdateChatMiddlewareCommand) => client.updateChatMiddleware(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getChatMiddlewares', { fieldId }]
      })
    }
  })
  const getEligibleCogfyCreditsProviders = useQuery({
    queryKey: ['getEligibleCogfyCreditsProviders', {}] as const,
    queryFn: ({ signal }) => client.getEligibleCogfyCreditsProviders({ signal })
  })
  const getConnections = useQuery({
    queryKey: ['getConnectionsList', {}] as const,
    queryFn: ({ queryKey, signal }) => client.getConnectionsList(queryKey[1], { signal })
  })
  const getCollections = useCollections({ type: 'database' })
  const eligibleCogfyCreditsProviders = getEligibleCogfyCreditsProviders.data?.data ?? []
  const connections = getConnections.data?.data ?? []
  const collections = getCollections.data?.data ?? []
  const getChatMiddlewareSchemas = useQuery({
    queryKey: ['getChatMiddlewareSchemas'],
    queryFn: ({ signal }) => client.getChatMiddlewareSchemas({ signal })
  })
  const update = useDebouncedCallback(() => {
    updateMiddleware.mutate({
      chatMiddlewareId: middleware.id,
      data,
      condition,
      collectionId,
      fieldId
    })
  }, 300)

  const middlewareSchema = useMemo(
    () => getChatMiddlewareSchemas.data?.data
      .find(schema => schema.name === middleware.type),
    [getChatMiddlewareSchemas.data, middleware.type]
  )

  // Tool use the schema only for the tool configs, in addition to the name and description
  // Retrieval is legacy
  const autogeneratedForm =
    middleware.type !== 'tool' && middleware.type !== 'retrieval'

  return (
    <div>
      <ChatMiddlewareConditionForm
        fields={props.fields}
        value={condition}
        onChange={newCondition => {
          setCondition(newCondition)
          update()
        }}
      />

      {middlewareSchema && autogeneratedForm && (
        <AutogeneratedForm
          collectionId={collectionId}
          fieldId={fieldId}
          value={data}
          onChange={newData => {
            setData(newData)
            update()
          }}
          schema={middlewareSchema.schema.inputs}
          fields={props.fields}
          connections={connections}
          eligibleCogfyCreditsProviders={eligibleCogfyCreditsProviders}
          collections={collections}
          getConnections={getConnections}
        />
      )}

      {(!middlewareSchema || !autogeneratedForm) && (
        <ChatMiddlewareDataForm
          type={middleware.type}
          collectionId={collectionId}
          fieldId={fieldId}
          data={data}
          onChange={newData => {
            setData(newData)
            update()
          }}
          schemas={getChatMiddlewareSchemas.data?.data}
          fields={props.fields}
          collections={props.collections}
        />
      )}
    </div>
  )
}
